import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { X, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { createContact, updateContactBuyerPreferences } from '../../../api/contact';
import { getProperties, getAgents } from '../../../api/agent';
import { addPropertyBuyer } from '../../../api/propertyManagement';
import { hasAdminAccess } from '../../../utils/roleAccess';
import { SuburbPicker } from '../../analytics/components/BuyerPreferenceFields';
import BuyerSpecFields, { EMPTY_SPEC, COMMERCIAL_TYPE } from './BuyerSpecFields';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  suburbs: [],
  budgetMin: '',
  budgetMax: '',
  ...EMPTY_SPEC,
};

const propertyLabel = (p) => p.title || p.address || `Property #${p.id}`;

/**
 * Quick-entry for a buyer who isn't tied to any property yet — e.g. a cold
 * call asking about a suburb and budget, with no listing to assign them to.
 * Creates the Contact and, if any preferences were given, saves them in the
 * same step (writes through to the same BuyingPreference record the agent's
 * profile-page editor and the buyer portal use).
 */
export default function AddBuyerModal({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Assigned agent (DOMA 13261735033). Admin-controlled: only admins pick who owns
  // the buyer; a regular agent who adds one is stamped as the agent server-side.
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  useEffect(() => {
    if (!isAdmin) return;
    getAgents({ role: 'agent', page_size: 200 })
      .then((res) => setAgents(res.data?.results || res.data || []))
      .catch(() => setAgents([]));
  }, [isAdmin]);

  // Property of interest — optional, so agents can also match the enquiry
  // straight to one of our current listings instead of only free-text suburbs.
  const [propertyQuery, setPropertyQuery] = useState('');
  const [propertySuggestions, setPropertySuggestions] = useState([]);
  const [showPropertySuggestions, setShowPropertySuggestions] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [searchingProperties, setSearchingProperties] = useState(false);
  const propertySearchTimer = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpec = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onPropertyQueryChange = (e) => {
    const value = e.target.value;
    setPropertyQuery(value);
    setSelectedProperty(null);
    if (propertySearchTimer.current) clearTimeout(propertySearchTimer.current);
    const q = value.trim();
    if (q.length < 2) {
      setPropertySuggestions([]);
      setShowPropertySuggestions(false);
      return;
    }
    propertySearchTimer.current = setTimeout(async () => {
      setSearchingProperties(true);
      try {
        const res = await getProperties({ search: q, page_size: 8 });
        const results = res?.data?.results || res?.data || [];
        setPropertySuggestions(Array.isArray(results) ? results : []);
        setShowPropertySuggestions(true);
      } catch {
        setPropertySuggestions([]);
      } finally {
        setSearchingProperties(false);
      }
    }, 350);
  };

  const pickProperty = (property) => {
    setSelectedProperty(property);
    setPropertyQuery(propertyLabel(property));
    setShowPropertySuggestions(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    if (!first && !last) {
      toast.error('Please enter a first name.');
      return;
    }

    setSaving(true);
    try {
      const { data: contact } = await createContact({
        first_name: first,
        last_name: last,
        name: [first, last].filter(Boolean).join(' '),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        contact_type: 'buyer',
        ...(isAdmin && agentId ? { agent: Number(agentId) } : {}),
      });

      const budgetMin = form.budgetMin.trim();
      const budgetMax = form.budgetMax.trim();
      // Only persist spec fields the agent actually set (non-"Any"), and send
      // Floor Area for Commercial or Bedrooms/Bathrooms/Car Spaces otherwise -
      // mirroring the full buyer-preferences editor's Commercial swap.
      const spec = {};
      if (form.propertyType.length > 0) spec.property_type = form.propertyType;
      if (form.propertyType.includes(COMMERCIAL_TYPE)) {
        if (form.floorAreaMin && form.floorAreaMin !== 'Any') spec.floor_area_min = form.floorAreaMin;
        if (form.floorAreaMax && form.floorAreaMax !== 'Any') spec.floor_area_max = form.floorAreaMax;
      } else {
        if (form.bedroomsMin && form.bedroomsMin !== 'Any') spec.bedrooms_min = form.bedroomsMin;
        if (form.bedroomsMax && form.bedroomsMax !== 'Any') spec.bedrooms_max = form.bedroomsMax;
        if (form.bathrooms && form.bathrooms !== 'Any') spec.bathrooms = form.bathrooms;
        if (form.carSpaces && form.carSpaces !== 'Any') spec.car_spaces = form.carSpaces;
      }
      const hasPreferences = form.suburbs.length > 0 || budgetMin || budgetMax || Object.keys(spec).length > 0;
      if (hasPreferences) {
        await updateContactBuyerPreferences(contact.id, {
          suburbs: form.suburbs,
          ...(budgetMin ? { price_min: budgetMin } : {}),
          ...(budgetMax ? { price_max: budgetMax } : {}),
          ...spec,
        });
      }

      if (selectedProperty) {
        try {
          await addPropertyBuyer(selectedProperty.id, { contact_id: contact.id });
        } catch {
          toast.error('Buyer added, but could not attach them to that property.');
        }
      }

      toast.success('Buyer added.');
      onCreated?.(contact);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail
        || err?.response?.data?.email?.[0]
        || err?.response?.data?.name?.[0];
      toast.error(detail || 'Failed to add buyer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-base font-bold text-gray-900">Add Buyer</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500 -mt-1">
            For a buyer who isn't tied to a listing yet — e.g. a call-in enquiry about a suburb and budget.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">First name</label>
              <input value={form.first_name} onChange={(e) => set('first_name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Last name</label>
              <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Phone</label>
            <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Email</label>
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25" />
          </div>

          {isAdmin && (
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Assigned agent</label>
              <select
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 bg-white"
              >
                <option value="">Unassigned</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.email}</option>
                ))}
              </select>
            </div>
          )}

          <div className="relative">
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Property of interest</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={propertyQuery}
                onChange={onPropertyQueryChange}
                onFocus={() => { if (propertySuggestions.length > 0) setShowPropertySuggestions(true); }}
                onBlur={() => setTimeout(() => setShowPropertySuggestions(false), 150)}
                placeholder="Search a listing we have for sale…"
                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25"
              />
            </div>
            {showPropertySuggestions && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searchingProperties && (
                  <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>
                )}
                {!searchingProperties && propertySuggestions.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400">No matching properties.</div>
                )}
                {!searchingProperties && propertySuggestions.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => pickProperty(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div className="font-medium text-gray-800">{propertyLabel(p)}</div>
                    {p.suburb && <div className="text-xs text-gray-400">{p.suburb}</div>}
                  </button>
                ))}
              </div>
            )}
            {selectedProperty && (
              <p className="text-xs text-emerald-600 mt-1">
                Will attach {form.first_name || 'this buyer'} to {propertyLabel(selectedProperty)}'s Buyers tab.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Suburbs of interest</label>
            <SuburbPicker value={form.suburbs} onChange={(v) => set('suburbs', v)} />
          </div>

          <BuyerSpecFields value={form} onChange={setSpec} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Budget min</label>
              <input value={form.budgetMin} onChange={(e) => set('budgetMin', e.target.value)}
                placeholder="$500,000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Budget max</label>
              <input value={form.budgetMax} onChange={(e) => set('budgetMax', e.target.value)}
                placeholder="$1,500,000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border rounded-md">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-sm bg-[#55C5D0] hover:bg-[#3aa8b4] disabled:opacity-60 text-white rounded-md font-semibold">
              {saving ? 'Saving…' : 'Add Buyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
